import { GraphQLError } from "graphql"
import { groq } from "next-sanity"

import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getCustomer } from "../../shared/utils/getCustomer"
import { getStoreKeysWithChannels } from "../../shared/utils/getStoreKeysWithChannels"
import { getInsertActions, updateCustomerCart } from "../utils/cartHelpers"
import { getActiveCustomerCart } from "../utils/getCart"
import { getItemsWithSelections } from "../utils/getItemsWithSelections"

export const mutationAddScanCustomLineItems = {
  async addScanCustomLineItems(_parent, args, context, _info) {
    let { customerId, eanCode, sku, lang, cdn, branchId } = args

    if (!eanCode && !sku) {
      return { cart: null, scannedItems: 0 }
    }

    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    const commerceApi = CommerceService.api()

    const customerCarts = await getActiveCustomerCart(customerId, user.employeeNumber)
    const customerCart = customerCarts[0]

    if (!customerCart || customerCart.cartState !== "Active") {
      throw new Error("Cart was not found or is not in active state")
    }

    // custom type created for custom line custom fields
    const customTypeLineItem = (
      await commerceApi.types().withKey({ key: "customCustomLineItemTypeKey" }).get().execute()
    )?.body

    if (!customTypeLineItem) {
      throw new Error("Custom type for custom line item was not found")
    }

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const customer = await getCustomer({ id: customerId })

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const { stores: customerStores, customerGroup } = customer

    const storeKeysWithChannels = await getStoreKeysWithChannels(customerStores)

    const totalItems = customerCart?.customLineItems?.length || 0
    const items = await getItemsForScanToCart({ eanCode, sku, client, branchId })
    let itemsWithSelections = await getItemsWithSelections(items, totalItems)

    const numOfItemsWithSelections = itemsWithSelections.length

    if (itemsWithSelections.length < 1) return { cart: null, scannedItems: 0 }
    const totalProjectedItems = Number(itemsWithSelections.length) + Number(customerCart?.customLineItems?.length || 0)
    if (totalProjectedItems > 200) {
      throw new GraphQLError("TOO_MANY_ITEMS", {
        extensions: {
          code: "TOO_MANY_ITEMS",
        },
      })
    }

    const actions = await getInsertActions({
      itemsWithSelections,
      customerCart,
      storeKeysWithChannels,
      customerGroup,
      currencyCode: customerCart.totalPrice.currencyCode,
      customTypeLineItem,
      isScanToCart: true,
    })

    const updatedCart = await updateCustomerCart({ customerCart, actions })

    return { cart: updatedCart, scannedItems: numOfItemsWithSelections }
  },
}

/**
 * Maps Sanity article results into cart insert items (incl. optional 2K article)
 *
 * @param {array} articleResults
 * @returns {{ sku: string, productKey: string, quantity: number, allowMultiple: boolean }[]}
 */
const mapArticlesToCartItems = (articleResults) => {
  const articles = []

  for (const item of articleResults) {
    if (!item || !item?.productRef?.key || !item?.sku) {
      continue
    }

    articles.push({
      sku: item.sku,
      productKey: item.productRef.key,
      quantity: 1,
      allowMultiple: true,
    })

    if (item?.twoKArticle?.sku && item?.twoKArticle?.productRef?.key) {
      articles.push({
        sku: item.twoKArticle.sku,
        productKey: item.twoKArticle.productRef.key,
        quantity: 1,
        allowMultiple: true,
      })
    }
  }

  return articles
}

/**
 * Retrieves articles from Sanity by SKU or EAN for scan-to-cart
 *
 * @param {{ eanCode?: string, sku?: string, client: object, branchId: string }}
 * @returns {Promise<{ sku: string, productKey: string, quantity: number, allowMultiple: boolean }[]>}
 */
const getItemsForScanToCart = async ({ eanCode, sku, client, branchId }) => {
  const draftsFilter = `&& !(_id in path("drafts.**"))`
  const matchFilter = sku ? `sku==$sku` : `(ean==$eanCode || altEAN==$eanCode)`

  const articleProjection = `{
    sku,
    "productRef": *[_type=="product" && references(^._id)][0] {
      key
    },
    twoKArticle->{
      sku,
      "productRef": *[_type=="product" && references(^._id)][0] {
        key
      }
    }
  }`

  const article = await client.fetch(
    groq`*[_type=="article" && ${matchFilter} && !("branchOffice-${branchId}" in notAvailableInBranchOffices[]._ref) ${draftsFilter}] ${articleProjection}`,
    sku ? { sku } : { eanCode },
  )

  return mapArticlesToCartItems(article)
}
